import React, { useEffect, useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { fetchProjects } from "../../Redux/WhatsappSlice/Project";
import {
    exeupload,
    ProjWiseClient,
    clearUploadData,
    notifyUpdate,
} from "../../Redux/Slice/DbExeUpdate";
import {
    FormControl,
    InputLabel,
    ListItemText,
    MenuItem,
    OutlinedInput,
    Select,
    Checkbox,
    CircularProgress,
    Box,
    IconButton,
} from "@mui/material";
import { toast } from 'react-toastify';
import 'react-toastify/dist/ReactToastify.css';
import Loader from "../../Components/Loader/Loader";
import { FiChevronDown, FiChevronUp } from "react-icons/fi";

const ExeUpdate = () => {
    const dispatch = useDispatch();

    const { ProjectData, loading: projectsLoading } = useSelector(
        (state) => state.Project
    );

    const {
        exeuploadLoading,
        notifyUpdateLoading,
        projWiseClients,
        projWiseClientsLoading,
        projWiseClientsError,
    } = useSelector((state) => state.DbExeUpdate);

    // Separate state for Notify section
    const [notifyFormData, setNotifyFormData] = useState({
        clientids: [],
        projectid: "",
    });

    // Separate state for Upload EXE section
    const [uploadFormData, setUploadFormData] = useState({
        uploadFiles: null,
        description: "",
    });

    const [clients, setClients] = useState([]);
    const [isInitialLoading, setIsInitialLoading] = useState(true);

    // State for select dropdown open/close
    const [openProjectSelect, setOpenProjectSelect] = useState(false);
    const [openClientSelect, setOpenClientSelect] = useState(false);

    useEffect(() => {
        const loadData = async () => {
            setIsInitialLoading(true);
            try {
                await dispatch(fetchProjects());
            } finally {
                setIsInitialLoading(false);
            }
        };
        loadData();
    }, [dispatch]);

    // Effect for Notify section
    useEffect(() => {
        if (notifyFormData.projectid) {
            dispatch(ProjWiseClient(notifyFormData.projectid));
        } else {
            setClients([]);
            setNotifyFormData((prev) => ({ ...prev, clientids: [] }));
        }
    }, [notifyFormData.projectid, dispatch]);

    useEffect(() => {
        if (projWiseClientsLoading) return;

        if (!projWiseClients) {
            setClients([]);
            return;
        }

        if (projWiseClients?.status === "Fail") {
            console.error("API Error:", projWiseClients?.message);
            setClients([]);
            return;
        }

        const clientList = Array.isArray(projWiseClients)
            ? projWiseClients
            : Array.isArray(projWiseClients?.data)
                ? projWiseClients.data
                : [];

        const processed = clientList.map((client) => ({
            id: String(client._id || client.id || client.clientId || ""),
            name: client.WhaClientName || client.name || client.clientName || "Unnamed Client",
        }));

        // Update clients for notify section
        setClients(processed);
    }, [projWiseClients, projWiseClientsLoading]);

    const menuProps = {
        PaperProps: {
            style: {
                maxHeight: 300,
                width: 350,
            },
        },
        disablePortal: true,
    };

    // Handlers for Notify section
    const handleNotifyProjectChange = (event) => {
        const projectid = event.target.value;
        setNotifyFormData({
            clientids: [],
            projectid: projectid,
        });
        setClients([]);
    };

    const handleNotifyClientChange = (event) => {
        const { value } = event.target;

        if (value[value.length - 1] === "all") {
            const allSelected = notifyFormData.clientids.length === clients.length;
            const newValue = allSelected ? [] : clients.map((client) => client.id);
            setNotifyFormData((prev) => ({ ...prev, clientids: newValue }));
            return;
        }

        setNotifyFormData((prev) => ({ ...prev, clientids: value }));
    };

    const renderNotifyClientValue = (selected) => {
        if (!selected || selected.length === 0) {
            return "Select clients";
        }

        const selectedCount = selected.length;
        if (selectedCount > 3) {
            return `${selectedCount} clients selected`;
        }

        const names = selected
            .map((id) => {
                const client = clients.find((c) => c.id === id);
                return client ? client.name : "";
            })
            .filter((name) => name);

        return names.join(", ");
    };

    const renderClientMenuItems = (selectedIds) => {
        if (projWiseClientsLoading) {
            return (
                <MenuItem disabled>
                    <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                        <CircularProgress size={20} />
                        <ListItemText primary="Loading clients..." style={{ marginLeft: 10 }} />
                    </Box>
                </MenuItem>
            );
        }

        if (!notifyFormData.projectid) {
            return (
                <MenuItem value="">
                    <ListItemText primary="No project chosen — showing no clients" />
                </MenuItem>
            );
        }

        if (projWiseClientsError) {
            return (
                <MenuItem disabled>
                    <ListItemText primary="Error loading clients" secondary={projWiseClientsError} />
                </MenuItem>
            );
        }

        if (!clients || clients.length === 0) {
            return (
                <MenuItem value="">
                    <ListItemText primary="No clients found for this project" />
                </MenuItem>
            );
        }

        return [
            <MenuItem key="select-all" value="all">
                <Checkbox
                    checked={clients.length > 0 && selectedIds.length === clients.length}
                    indeterminate={
                        selectedIds.length > 0 && selectedIds.length < clients.length
                    }
                />
                <ListItemText primary={`Select All (${clients.length})`} />
            </MenuItem>,
            ...(Array.isArray(clients) ? clients : []).map((client) => (
                <MenuItem key={client.id} value={client.id}>
                    <Checkbox checked={selectedIds.includes(client.id)} />
                    <ListItemText primary={client.name} />
                </MenuItem>
            )),
        ];
    };

    const renderProjectMenuItems = () => {
        if (projectsLoading) {
            return (
                <MenuItem disabled>
                    <Box display="flex" alignItems="center" justifyContent="center" width="100%">
                        <CircularProgress size={20} />
                        <ListItemText primary="Loading projects..." style={{ marginLeft: 10 }} />
                    </Box>
                </MenuItem>
            );
        }

        if (!ProjectData || !Array.isArray(ProjectData) || ProjectData.length === 0) {
            return <MenuItem value="">No projects available</MenuItem>;
        }

        return ProjectData.map((project) => (
            <MenuItem key={project._id || project.id} value={project._id || project.id}>
                {project.project_name || project.name || "Unnamed Project"}
            </MenuItem>
        ));
    };

    const handleExeUpload = () => {
        if (!uploadFormData.uploadFiles || uploadFormData.uploadFiles.length === 0) {
            toast.warning("Please select EXE files!");
            return;
        }

        const fd = new FormData();
        Array.from(uploadFormData.uploadFiles).forEach((file) => {
            fd.append("uploadFiles", file);
        });

        if (uploadFormData.description) {
            fd.append("Description", uploadFormData.description);
        }

        dispatch(exeupload(fd)).then(() => {
            setUploadFormData({
                uploadFiles: null,
                description: ""
            });
            setTimeout(() => dispatch(clearUploadData()), 2000);
            toast.success("EXE uploaded successfully!");
        }).catch(() => {
            toast.error("Failed to upload EXE!");
        });
    };

    const handleNotifyUpdate = () => {
        if (!notifyFormData.clientids.length) {
            toast.warning("Please select at least one client!");
            return;
        }

        const notifyData = {
            clientids: notifyFormData.clientids || [],
            projectid: notifyFormData.projectid || "",
        };

        dispatch(notifyUpdate(notifyData)).then(() => {
            setTimeout(() => dispatch(clearUploadData()), 2000);
            toast.success("Update notification sent successfully!");
        }).catch(() => {
            toast.error("Failed to send update notification!");
        });
    };

    const resetNotifyForm = () => {
        setNotifyFormData({
            clientids: [],
            projectid: "",
        });
        setClients([]);
        toast.info("Notify form cleared!");
    };

    const resetUploadForm = () => {
        setUploadFormData({
            uploadFiles: null,
            description: "",
        });
        toast.info("Upload form cleared!");
    };

    // Show loader during initial data loading
    if (isInitialLoading || projectsLoading) {
        return <Loader />;
    }

    return (
        <div className="w-full p-4 space-y-6">

            {/* Select Notify Update Section */}
            <div className="rounded-xl p-6 shadow-lg bg-white">
                <div className="title mb-5">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        Select Notify Update
                    </h3>
                </div>
                <div className="grid grid-cols-1 md:grid-cols-12 gap-6 items-start">



                    <div className="md:col-span-4 min-h-[85px]">
                        <FormControl fullWidth size="medium">
                            <InputLabel id="notify-project-select-label">
                                Select Project
                            </InputLabel>

                            <Select
                                labelId="notify-project-select-label"
                                open={openProjectSelect}               // ✅ control open
                                value={notifyFormData.projectid}
                                onChange={handleNotifyProjectChange}
                                onOpen={() => setOpenProjectSelect(true)}
                                onClose={() => setOpenProjectSelect(false)}
                                input={
                                    <OutlinedInput
                                        label="Select Project"
                                        sx={{
                                            backgroundColor: "#F3F8FB",
                                            borderRadius: "8px",
                                            padding: "6px",
                                            "& fieldset": {
                                                borderColor: "black",
                                                borderWidth: "2px",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "black",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "black",
                                            },
                                        }}
                                        endAdornment={
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();          // ✅ important
                                                    setOpenProjectSelect((prev) => !prev);
                                                }}
                                                sx={{
                                                    padding: 0,
                                                    marginRight: "8px",
                                                    color: "black",
                                                }}
                                            >
                                                {openProjectSelect ? (
                                                    <FiChevronUp size={20} />
                                                ) : (
                                                    <FiChevronDown size={20} />
                                                )}
                                            </IconButton>
                                        }
                                    />
                                }
                                MenuProps={menuProps}
                                IconComponent={() => null}               // hide default MUI arrow
                            >
                                {renderProjectMenuItems()}
                            </Select>
                        </FormControl>
                    </div>


                    <div className="md:col-span-4 min-h-[105px]">
                        <FormControl fullWidth size="medium">
                            <InputLabel id="notify-client-select-label">
                                Select Clients
                            </InputLabel>

                            <Select
                                labelId="notify-client-select-label"
                                multiple
                                open={openClientSelect}
                                value={notifyFormData.clientids}
                                onChange={handleNotifyClientChange}
                                onOpen={() => setOpenClientSelect(true)}
                                onClose={() => setOpenClientSelect(false)}
                                renderValue={renderNotifyClientValue}
                                input={
                                    <OutlinedInput
                                        label="Select Clients"
                                        sx={{
                                            backgroundColor: "#F3F8FB",
                                            borderRadius: "8px",
                                            padding: "6px",
                                            "& fieldset": {
                                                borderColor: "black",
                                                borderWidth: "2px",
                                            },
                                            "&:hover fieldset": {
                                                borderColor: "black",
                                            },
                                            "&.Mui-focused fieldset": {
                                                borderColor: "black",
                                            },
                                        }}
                                        endAdornment={
                                            <IconButton
                                                onClick={(e) => {
                                                    e.stopPropagation();
                                                    setOpenClientSelect((prev) => !prev);
                                                }}
                                                sx={{
                                                    padding: 0,
                                                    marginRight: "8px",
                                                    color: "black",
                                                }}
                                            >
                                                {openClientSelect ? (
                                                    <FiChevronUp size={20} />
                                                ) : (
                                                    <FiChevronDown size={20} />
                                                )}
                                            </IconButton>
                                        }
                                    />
                                }
                                MenuProps={menuProps}
                                IconComponent={() => null}
                            >
                                {renderClientMenuItems(notifyFormData.clientids)}
                            </Select>
                        </FormControl>

                        {projWiseClientsLoading && (
                            <div className="flex items-center mt-2 text-blue-600">
                                <CircularProgress size={16} className="mr-2" />
                                <span className="text-sm">Loading clients...</span>
                            </div>
                        )}

                        {projWiseClientsError && (
                            <div className="mt-2 text-red-600 text-sm">
                                Error: {projWiseClientsError}
                            </div>
                        )}

                        {notifyFormData.projectid &&
                            !projWiseClientsLoading &&
                            !projWiseClientsError && (
                                <div className="mt-2 text-gray-600 text-sm">
                                    {clients.length} client(s) available •{" "}
                                    {notifyFormData.clientids.length} selected
                                </div>
                            )}
                    </div>





                    <div className="md:col-span-2 flex items-center">
                        <button
                            onClick={handleNotifyUpdate}
                            disabled={notifyUpdateLoading || !notifyFormData.clientids.length}
                            className={`${notifyUpdateLoading || !notifyFormData.clientids.length
                                ? 'bg-gray-400 cursor-not-allowed'
                                : 'bg-[#122A47] hover:bg-[#0c2545]'
                                } text-white py-2 px-4 rounded-md transition-colors w-full`}
                        >
                            {notifyUpdateLoading ? (
                                <span className="flex items-center justify-center">
                                    <CircularProgress size={20} className="mr-2 text-white" />
                                    Sending...
                                </span>
                            ) : (
                                "Notify Update"
                            )}
                        </button>
                    </div>

                    <div className="md:col-span-2 flex items-center">
                        <button
                            onClick={resetNotifyForm}
                            className="bg-gray-600 text-white py-2 px-4 rounded-md hover:bg-gray-700 transition-colors w-full"
                        >
                            Clear All
                        </button>
                    </div>
                </div>

                {/* Badges for Notify Section */}
                <div className="mt-4 pt-4 border-t border-gray-200">
                    <div className="flex flex-wrap gap-2">
                        {notifyFormData.projectid && (
                            <span className="px-3 py-1 bg-purple-100 text-purple-800 rounded-full text-sm font-medium">
                                Project Selected
                            </span>
                        )}

                        {notifyFormData.clientids.length > 0 && (
                            <span className="px-3 py-1 bg-blue-100 text-blue-800 rounded-full text-sm font-medium">
                                {notifyFormData.clientids.length} client{notifyFormData.clientids.length !== 1 ? "s" : ""}
                            </span>
                        )}
                    </div>
                </div>
            </div>

            {/* Upload EXE Section */}
            <div className="rounded-xl p-6 shadow-lg bg-white">
                <div className="title mb-3">
                    <h3 className="text-xl font-bold main_title_page text-white px-4 py-2 inline-block rounded">
                        Upload EXE
                    </h3>
                </div>

                <div className="mb-6">
                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4 mb-4">
                        <div className="md:col-span-4">
                            <input
                                type="file"
                                multiple
                                accept=".exe,.zip,.rar"
                                className="bg-[#F3F8FB] w-full p-2 border-2 border-black rounded"
                                onChange={(e) =>
                                    setUploadFormData((prev) => ({
                                        ...prev,
                                        uploadFiles: e.target.files,
                                    }))
                                }
                            />
                        </div>
                    </div>

                    <div className="grid grid-cols-1 md:grid-cols-12 gap-4">
                        <div className="md:col-span-12">
                            <textarea
                                placeholder="Enter description..."
                                className="bg-[#F3F8FB] w-full p-3 border-2 border-black rounded resize-y min-h-[120px]"
                                value={uploadFormData.description}
                                onChange={(e) =>
                                    setUploadFormData((prev) => ({
                                        ...prev,
                                        description: e.target.value,
                                    }))
                                }
                            ></textarea>
                        </div>

                        <div className="md:col-span-12 flex justify-end">
                            <button
                                onClick={handleExeUpload}
                                disabled={exeuploadLoading || !uploadFormData.uploadFiles || uploadFormData.uploadFiles.length === 0}
                                className={`${exeuploadLoading || !uploadFormData.uploadFiles || uploadFormData.uploadFiles.length === 0
                                    ? 'bg-gray-400 cursor-not-allowed'
                                    : 'bg-[#122A47] hover:bg-[#0c2545]'
                                    } text-white py-2 px-4 rounded-md transition-colors`}
                            >
                                {exeuploadLoading ? (
                                    <span className="flex items-center">
                                        <CircularProgress size={20} className="mr-2 text-white" />
                                        Uploading...
                                    </span>
                                ) : (
                                    "Upload EXE"
                                )}
                            </button>
                        </div>
                    </div>
                </div>

                {/* Badges Section for Upload */}
                <div className="mt-6 pt-4 border-t border-gray-200">
                    <div className="flex flex-wrap gap-2">
                        {uploadFormData.uploadFiles?.length > 0 && (
                            <span className="px-3 py-1 bg-orange-100 text-orange-800 rounded-full text-sm font-medium">
                                {uploadFormData.uploadFiles.length} EXE file
                                {uploadFormData.uploadFiles.length !== 1 ? "s" : ""}
                            </span>
                        )}

                        {uploadFormData.description && (
                            <span className="px-3 py-1 bg-green-100 text-green-800 rounded-full text-sm font-medium">
                                Description added
                            </span>
                        )}
                    </div>
                </div>
            </div>
        </div>
    );
};

export default ExeUpdate;